import React, { useState, useEffect, useRef } from "react";
import CustomButton from "../pages/CustomButton";
import {
  fetchStates,
  fetchEmployees,
  loadScheme,
  fetchDivisions,
  fetchEmployeeMonthlySchemeReport,
} from "../services/stockistService";
import formatDate from "../utils/formatDate";
import Loader from "../dist/Loader";

const Scheme = () => {
  const currentDate = new Date();

  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");

  const [schemeTypes, setSchemeTypes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState("");

  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("");

  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    currentDate.getMonth() + 1,
  );

  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);

  const tableRef = useRef(null);
  const years = [2024, 2025, 2026];
  const months = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" },
  ];

  // Load States
  useEffect(() => {
    const loadStates = async () => {
      try {
        const list = await fetchStates();
        setStates(list);
      } catch {
        console.log("Failed to load states");
      }
    };
    loadStates();
  }, []);

  // Load Employees when State changes
  useEffect(() => {
    const loadEmployees = async (stateId) => {
      if (!stateId) return;
      try {
        const list = await fetchEmployees(stateId);
        setEmployees(list);
      } catch {
        console.log("Failed to load employees");
      }
    };
    loadEmployees(selectedState);
  }, [selectedState]);

  // Load Schemes when State changes
  useEffect(() => {
    const loadSchemeList = async (stateId) => {
      if (!stateId) return;
      try {
        const list = await loadScheme(stateId);
        setSchemeTypes(list);
      } catch {
        console.log("Failed to load schemes");
      }
    };
    loadSchemeList(selectedState);
  }, [selectedState]);

  // Load Divisions (no state dependency)
  useEffect(() => {
    const loadDivisions = async () => {
      try {
        const list = await fetchDivisions();
        setDivisions(list);
      } catch {
        console.log("Failed to load divisions");
      }
    };
    loadDivisions();
  }, []);

  // GO BUTTON CLICK
  const handleGoClick = async () => {
    if (!selectedState || !selectedEmployee) {
      alert("Please select state and employee");
      return;
    }

    const divisionName =
      divisions.find((d) => d.id === parseInt(selectedDivision))?.name || "";

    const payload = {
      state_id: parseInt(selectedState),
      year: parseInt(selectedYear),
      month: parseInt(selectedMonth),
      division: divisionName,
      employee_id: parseInt(selectedEmployee),
      scheme_id: selectedScheme ? parseInt(selectedScheme) : 0,
    };

    console.log("PAYLOAD SENDING → ", payload);

    setLoading(true);
 

try {
  await new Promise(resolve => setTimeout(resolve, 3000));
  const data = await fetchEmployeeMonthlySchemeReport(payload);
      setReport(data || []);
    } catch (err) {
      console.error("Failed to fetch report", err);
      alert("Failed to fetch report. Check console for details.");
      setReport([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!tableRef.current) {
      alert("No table to print");
      return;
    }

    const tableHtml = tableRef.current.outerHTML;
    const style = `
      <style>
        body { font-family: Inter, Arial, sans-serif; padding: 12px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #1f2937; padding: 6px 8px; font-size: 12px; line-height: 1.2; }
        thead { background: #2563eb; color: white; }
        tbody tr:nth-child(even) { background: #f8fafc; }
      </style>
    `;

    const printWindow = window.open("", "_blank", "width=1000,height=700");
    if (!printWindow) {
      alert("Unable to open print window. Check popup settings.");
      return;
    }
    printWindow.document.write(
      `<html><head><title>Print report</title>${style}</head><body>`,
    );
    printWindow.document.write(tableHtml);
    printWindow.document.write("</body></html>");
    printWindow.document.close();

    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  // Export CSV: convert report array to CSV and trigger download
  const handleExportExcel = () => {
    if (!report || !report.length) {
      alert("No data to export");
      return;
    }

    const headers = [
      "S.No",
      "Emp ID",
      "State",
      "Employee Name",
      "Designation",
      "HQ",
      "Division",
      "Manager",
      "DOJ",
      "DOR",
      "Total Count",
      "Nov POB",
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = report.map((item, idx) => {
      const doj = formatDate ? formatDate(item.doj) : item.doj;
      return [
        idx + 1,
        item.emp_id ?? "",
        item.state ?? "",
        item.employee_name ?? "",
        item.designation ?? "",
        item.hq ?? "",
        item.division ?? "",
        item.manager ?? "",
        doj ?? "",
        item.dor ?? "",
        item.total_visits ?? "",
        item.total_pob ?? "",
      ];
    });

    const csvArray = [headers.map(escapeCsv).join(",")].concat(
      rows.map((r) => r.map(escapeCsv).join(",")),
    );
    const csvContent = csvArray.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const now = new Date();
    const filename = `scheme_report_${now.getFullYear()}${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}.csv`;

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {/* Loader overlay while loading */}
      {loading && <Loader onCancel={() => setLoading(false)} />}

      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <div>
          <h2 className="text-2xl font-bold flex justify-center  gap-2">
            <span className="text-blue-700">Scheme </span>
            <span className="text-blue-500">Report</span>
          </h2>
        </div>
        {/* ROW 1 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center">
          {/* State */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">State</label>
            <select
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          {/* Employee */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {`${emp.first_name} ${emp.last_name} || ${emp.designation} || ${emp.city_name}`.trim()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ROW 2 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center">
          {/* Scheme Type */}
          <div className="flex items-center gap-2 mr-12">
            <label className="font-medium whitespace-nowrap">Scheme Type</label>
            <select
              value={selectedScheme}
              onChange={(e) => setSelectedScheme(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Select Scheme</option>
              {schemeTypes.map((scheme) => (
                <option key={scheme.id} value={scheme.id}>
                  {scheme.scheme_name}
                </option>
              ))}
            </select>
          </div>

          {/* Division */}
          <div className="flex items-center gap-4 mt-5 ml-5">
            <label className="font-medium whitespace-nowrap">Division</label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Select Division</option>
              {divisions.map((div) => (
                <option key={div.id} value={div.id}>
                  {div.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ROW 3 */}
        <div className="grid grid-cols-2 gap-30 items-center justify-items-center">
          {/* Year */}
          <div className="flex items-center gap-4">
            <label className="font-medium whitespace-nowrap">Year</label>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div className="flex items-center gap-4 mt-5 ml-6">
            <label className="font-medium whitespace-nowrap">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border border-gray-300 rounded-md w-56 px-3 py-1"
            >
              <option value="">Month</option>
              {months.map((month) => (
                <option key={month.id} value={month.id}>
                  {month.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-center">
          <CustomButton variant="go" onClick={handleGoClick}>
          </CustomButton>
        </div>
      </div>

      {report.length > 0 && (
        <>
          {/* TABLE */}
          <div className="p-2  border-2 border-gray-300 rounded-lg bg-white  mt-5">
            <div className="border rounded shadow  table-wrapper">
              <table className="unlock-dcr-leave-table text-sm " ref={tableRef}>
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="border px-3 py-2">S.No</th>
                    <th className="border px-3 py-2">Emp ID</th>
                    <th className="border px-3 py-2">State</th>
                    <th className="border px-3 py-2">Employee Name</th>
                    <th className="border px-3 py-2">Designation</th>
                    <th className="border px-3 py-2">HQ</th>

                    <th className="border px-3 py-2">Division</th>
                    <th className="border px-3 py-2">Manager</th>
                    <th className="border px-3 py-2">DOJ</th>
                    <th className="border px-3 py-2">DOR</th>

                    <th className="border px-3 py-2">Total Count</th>
                    <th className="border px-3 py-2">Nov POB</th>
                  </tr>
                </thead>

                <tbody>
                  {report.map((item, index) => (
                    <tr key={index}>
                      <td className="border px-3 py-2">{index + 1}</td>
                      <td className="border px-3 py-2">{item.emp_id}</td>
                      <td className="border px-3 py-2">{item.state}</td>
                      <td className="border px-3 py-2">{item.employee_name}</td>
                      <td className="border px-3 py-2">{item.designation}</td>
                      <td className="border px-3 py-2">{item.hq}</td>
                      <td className="border px-3 py-2">{item.division}</td>
                      <td className="border px-3 py-2">{item.manager}</td>
                      <td className="border px-3 py-2">
                        {formatDate(item.doj) || ""}
                      </td>
                      <td className="border px-3 py-2">{item.dor || ""}</td>

                      <td className="border px-3 py-2">{item.total_visits}</td>
                      <td className="border px-3 py-2">{item.total_pob}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Scheme;
